<template>
  <div class="ba-pane-control">
    <div class="background"
         :class="{ disabled: reportShowing }"></div>

    <div class="feature zoom-control">
      <ba-slider :min="25"
                 :max="175"
                 :default="100"
                 :sliderStep="1"
                 :buttonStep="25"
                 :value="paneScale"
                 :vertical="true"
                 :minimal="true"
                 :disabled="noData"
                 @change="onPaneScale">
        <i class="tooltip">
          <span class="tooltip-text" v-html="$t('journey.control.zoom')"></span>
        </i>
      </ba-slider>
      <ba-button :icon="'icon--fit'"
                 :iconActive="'icon--fit white'"
                 :disabled="noData"
                 @click="onPaneScaleFit">
        <i class="tooltip">
          <span class="tooltip-text" v-html="$t('journey.control.fitToPage')"></span>
        </i>
      </ba-button>
    </div>

    <div class="feature see-all">
      <ba-button :icon="'icon--ratio'"
                 :iconActive="'icon--ratio white'"
                 :active="this.ratioOption === 'Overall flow'"
                 :disabled="noData"
                 @click="onChangeRatioOption">
        <i class="tooltip">
          <span class="tooltip-text" v-html="this.ratioOption === 'Overall flow' ? $t('journey.control.ratioOptionAll') : $t('journey.control.ratioOption')"></span>
        </i>
      </ba-button>
      <div class="ba-control-wrap selected">
        <ba-button :icon="'icon--check-options'"
                   :iconActive="'icon--check-options white'"
                   :keepActive="isVisibleOptionsVisible"
                   :disabled="noData"
                   @click="toggleVisibles('VisibleOptions')">
          <i class="tooltip">
            <span class="tooltip-text" v-html="$t('trend.control.visibleOptions')"></span>
          </i>
        </ba-button>
        <transition name="show">
          <div v-if="isVisibleOptionsVisible"
               class="ba-control-layer visible-options">
            <div class="switch"
                 :class="{
                  'is-on': seeAll
                 }">
              <span class="label"
                    @click="onChangeSeeAll(true)">
                {{ $t('common.all') }}
              </span>
              <span class="ba-switch-wrap"
                    @click="onChangeSeeAll(!seeAll)">
                <ba-switch :value="!seeAll"
                           :default="false"
                           :onColor="'#0e90a5'"
                           :offColor="'#3eaa08'"
                           :onLabel="''"
                           :offLabel="''"
                           :width="30"
                           :size="'xsmall'" />
              </span>
              <span class="label"
                    @click="onChangeSeeAll(false)">
                {{ $t('journey.control.keyIndicators') }}
              </span>
            </div>
            <ul class="list">
              <li v-for="(name, index) in ['sessioni', 'dropOff', 'conversion', 'rollback', 'refresh', 'duration', 'mptiLabel']"
                  :key="`visible-options-${index}`"
                  :class="`${ name.toLowerCase() }`">
                <svg width="16" height="16" viewBox="0 0 16 16">
                  <path d="m2.683 7.963 4.017 3.31 6.977-6.97"/>
                </svg>
                {{ $t((/^(sessioni|conversion)$/).test(name) ? 'common.' + name : 'trend.' + name) }}
              </li>
            </ul>
          </div>
        </transition>
      </div>
      <div class="ba-control-wrap" v-if="journeyOptions.matchType !== 'exact'"
           :class="{selected: grouping}">
        <ba-button :icon="'icon--grouping'"
                   :iconActive="'icon--grouping white'"
                   :keepActive="isGroupOptionsVisible"
                   :disabled="noData || journeyOptions.isCustomNode"
                   @click="toggleVisibles('GroupOptions')">
          <i class="tooltip">
            <span class="tooltip-text" v-html="$t('journey.control.group')"></span>
          </i>
        </ba-button>
        <transition name="show">
          <div v-if="isGroupOptionsVisible"
               class="ba-control-layer group-options">
            <span v-for="[key, name] in groupList"
                  :key="`group-${key}`"
                  class="ui-input-item"
                  :class="{spliter: key === 'none'}">
              <ui-radio :value="(
                          (!grouping && key === 'none') ||
                          (grouping && group === key)
                        )"
                        @input="onGroupChange(key)">
                {{ name }}
              </ui-radio>
            </span>
          </div>
        </transition>
      </div>
    </div>

    <div class="feature pluck">
      <ba-button :icon="'icon--pluck gray'"
                 :iconActive="'icon--pluck white'"
                 :active="pluck"
                 :disabled="noData"
                 @click="onChangePluck(!pluck)">
        <i class="tooltip">
          <span class="tooltip-text"
                v-html="$t('journey.control.pluck')"></span>
        </i>
      </ba-button>
    </div>

    <div class="feature excel">
      <ba-button :icon="'icon--excel'"
                 :iconActive="'icon--excel white'"
                 :disabled="noData"
                 @click="onDownloadExcel">
        <i class="tooltip">
          <span class="tooltip-text excel">
            <span class="excel-image"></span>
            <span class="excel-text">
              <span class="excel-title"
                    v-html="$t('journey.excel.title')"></span>
              <span class="excel-description"
                    v-html="$t('journey.excel.description')"></span>
            </span>
          </span>
        </i>
      </ba-button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { browserInfo } from '@/libs/utils'
import { EventBus } from '@/libs/event-bus'
import BaButton from '@/components/ui/ba/Button'
import BaSlider from '@/components/ui/ba/Slider'
import UiRadio from '@/components/ui/UiRadio'
import BaSwitch from '@/components/ui/ba/Switch'

export default {
  name: 'Journey',
  components: {
    BaButton,
    BaSlider,
    UiRadio,
    BaSwitch
  },
  props: ['paneScale', 'seeAll', 'grouping', 'group', 'groupItems', 'pluck', 'ratioOption', 'reportShowing'],
  data () {
    return {
      noData: false,
      tempMode: 'light',
      darkModeOptions: [
        {
          name: 'light',
          icon: 'icon--light black',
          iconActive: 'icon--light white',
          colorActive: '#45b5d8',
          label: 'Light',
          value: 'light',
          hideLabel: true
        },
        {
          name: 'dark',
          icon: 'icon--dark black',
          iconActive: 'icon--dark white',
          colorActive: '#5b548b',
          label: 'Dark',
          value: 'dark',
          hideLabel: true
        }
      ],
      listSizeOptions: [
        {
          name: '3x3',
          label: '2×3',
          value: {
            x: 3,
            y: 3
          }
        },
        {
          name: '7x6',
          label: '6×6',
          value: {
            x: 7,
            y: 6
          }
        }
      ],
      isVisibleOptionsVisible: false,
      isGroupOptionsVisible: false
    }
  },
  computed: {
    ...mapState([
      'planData',
      'journeyPeriod',
      'journeyOptions',
      'journeyPluck'
    ]),
    browser () {
      return browserInfo.name
    },
    groupList () {
      const list = Object.entries(this.groupItems)
      list.unshift(['none', this.$t('common.unapplied')])
      return list
    }
  },
  created () {
    EventBus.$on('journeyOptionChange', (key, value) => {
      if (key === 'listSize') {
        if (value.x > 6) {
          this.$emit('onChangeSeeAll', false)
        } else {
          this.$emit('onChangeSeeAll', true)
        }
      }
    })
    EventBus.$onSticky('loadPaneNoData', (value) => {
      this.noData = value
      EventBus.$clearSticky('loadPaneNoData')
    })

    this._visibles = ['VisibleOptions', 'GroupOptions']
  },
  mounted () {
    document.body.addEventListener('pointerdown', this.checkCloseControlLayers)
  },
  beforeDestroy () {
    document.body.removeEventListener('pointerdown', this.checkCloseControlLayers)
  },
  methods: {
    onChangeSeeAll (value) {
      this.$emit('onChangeSeeAll', value)
    },
    onChangePluck (value) {
      this.$emit('onChangePluck', value)
    },
    onChangeRatioOption () {
      if (this.ratioOption === 'Overall flow') {
        this.$emit('onChangeRatioOption', 'Stepwise flow')
      } else {
        this.$emit('onChangeRatioOption', 'Overall flow')
      }
    },
    onPaneScale (value) {
      this.$emit('onPaneScaleChange', value / 100)
    },
    onPaneScaleFit () {
      this.$emit('onPaneScaleFit')
    },
    onDownloadExcel () {
      this.$emit('onDownloadExcel')
    },

    checkCloseControlLayers (e) {
      const body = document.body
      let target = e.target
      while (target !== body) {
        if (target.classList.contains('ba-control-wrap')) {
          return
        }
        target = target.parentNode
      }
      this.closeAllControlLayers()
    },
    toggleVisibles (targetControlName, onlyForHideOthers) {
      this._visibles.forEach((controlName) => {
        const propertyName = 'is' + controlName + 'Visible'
        if (controlName === targetControlName) {
          this[propertyName] = onlyForHideOthers ? this[propertyName] : !this[propertyName]
        } else {
          this[propertyName] = false
        }
      })
    },
    closeAllControlLayers () {
      this.toggleVisibles('')
    },
    onChangeGrouping (value) {
      this.$emit('onChangeGrouping', value)
    },
    onGroupChange (value) {
      if (value === 'none') {
        if (this.grouping) {
          this.$emit('onChangeGrouping', false)
        }
      } else {
        this.$emit('onGroupChange', value)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ba-pane-control {
  height: 100%;
  padding: 2px 10px 10px;
  box-sizing: border-box;
  text-align: center;

  .background {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(244, 244, 241, 0.7);
    backdrop-filter: blur(15px); /* IE, FF(And), Samsung 미지원 */
    transition: background-color 0.3s ease;

    &.disabled {
      background-color: rgba(219, 219, 216, 0.7);
    }
  }

  .feature {
    position: relative;
    z-index: 2;
    display: inline-block;
    vertical-align: middle;
    margin: 5px 0;

    label {
      font-size: 13px;
      vertical-align: middle;
      margin: 0 8px 0 20px;
      font-weight: 500;
      color: #666666;
    }

    .ba-slider,
    .ba-switch,
    .ba-button,
    .ba-button-select {
      margin: 3px 0 2px 0;

      & /deep/ {
        .icon--b {
          position: absolute;
          top: -5px;
          right: -8px;
          left: auto;
          width: 19px;
          height: 19px;

          &.fade-enter-active,
          &.fade-leave-active {
            opacity: 1;
            transition: opacity 0.2s ease;
          }

          &.fade-enter,
          &.fade-leave-to{
            opacity: 0;
          }
        }

        .text {
          margin-left: 0;
        }

        .tooltip {
          position: absolute;
          top: calc(50% + 1px);
          left: 38px;
          transform: translate(-40px, -50%);
          opacity: 0;
          transition: transform 0.2s ease,
                      opacity 0.2s ease;
          pointer-events: none;
          padding: 30px 20px 30px 10px;

          &::after {
            content: '';
            background-color: #fff;
            position: absolute;
            z-index: 2;
            transform: rotate(-135deg);
            width: 8px;
            height: 8px;
            box-shadow: 1px -1px 0 0 rgba(0, 0, 0, 0.16);
            top: calc(50% - 4.5px);
            left: 6px;
          }

          .tooltip-text {
            border-radius: 6px;
            white-space: nowrap;
            box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16), 0 0 0 1px rgba(0, 0, 0, 0.16);
            background-color: #ffffff;
            padding: 8px 10px 9px;
            color: #444444 !important;
            font-size: 13px;
            line-height: 1;

            &.excel {
              padding: 44px 17px;

              .excel-image {
                display: inline-block;
                width: 83px;
                height: 57px;
                background: url('../../assets/img/journey/asset--excel-node.svg') no-repeat;
                background-size: 100% 100%;
                vertical-align: middle;
              }

              .excel-text {
                padding: 0 0 0 15px;
                display: inline-block;
                vertical-align: middle;
                font-size: 12px;
                line-height: 1.33;
                color: #767676;
                text-align: left;

                .excel-title {
                  font-size: 13px;
                  line-height: 1;
                  color: #444;
                  display: block;
                  margin-bottom: 10px;
                }

                .excel-description {
                  display: block;
                }
              }
            }
          }
        }
      }

      &:not(.ba-button):hover /deep/,
      &.ba-button:hover /deep/ button:not(.keep-active) {
        .tooltip {
          transform: translate(0, -50%);
          opacity: 1;
        }
      }

      &.noData {
        opacity: 0.3;
      }
    }

    .ba-button.seeall {
      & /deep/ .text {
        font-size: 11px;
        width: 16px;
        height: 16px;
        line-height: 14px;
        transform: translate(-1px, 2.5px);
      }
    }

    .ba-button-select {
      width: 32px;

      &.safari {
        margin-left: 2px;
        margin-right: 2px;
      }

      & /deep/ button {
        width: 32px;
        padding: 8px 0;
      }
    }

    .ui_select {
      transform: scale(0.825);
      vertical-align: middle;
      margin-right: -12px;

      & /deep/ {
        .item--wrap {
          border: none;
          box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
          transition: background-color 0.2s ease;

          &:hover {
            background-color: #efefef;
          }

          .item__list {
            border-color: rgba(0, 0, 0, 0.125);
          }
        }
      }
    }

    .ui-input-item {
      position: relative;
      height: 18px;
      white-space: nowrap;
      display: block;

      & + .ui-input-item {
        margin-top: 8px;
      }

      &.spliter + .ui-input-item {
        margin-top: 17px;
        &:before {
          position: relative;
          top: -9px;
          height: 0;
          content: '';
          margin-right: -5px;
          border-top: 1px solid #ebebeb;
          display: block;
        }
      }
    }

    .ui_radio {
      margin-left: 0;

      & /deep/ {
        .text {
          font-size: 12px;
          font-weight: 500;
          color: #555;
          line-height: 18px;
          margin-left: 24px;
        }
      }
    }

    &.list-size {
      .ba-button-select {
        position: relative;
        margin-bottom: 0;
      }

      .ba-button {
        margin-top: 0;

        &.for-free {
          opacity: 0.3;
        }

        & /deep/ button {
          border-top-right-radius: 0;
          border-top-left-radius: 0;
          opacity: 1;

          .icon {
            top: 4px;
            left: 4px;
            width: 24px;
            height: 24px;
          }
        }
      }
    }

    &.excel {
      &::before {
        content: "";
        display: block;
        width: 24px;
        height: 1px;
        margin: -2px 4px 7px;
        background-color: rgba(0, 0, 0, 0.12);
      }
    }
  }
}

.ba-control-wrap {
  position: relative;

  &.selected {
    .ba-button /deep/ button:after {
      position: absolute;
      right: 3px;
      bottom: 3px;
      width: 5px;
      height: 5px;
      content: '';
      border-radius: 100%;
      background: #64b950;
    }
  }

  .ba-control-layer {
    position: absolute;
    left: calc(100% + 5px);
    top: 3px;
    text-align: left;
    padding: 10px 15px 10px 10px;
    border-radius: 5px;
    background: #fff;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);

    &.visible-options {
      padding: 10px 10px 6px;

      .switch {
        font-size: 0;
        white-space: nowrap;
        padding: 0 14px 7px 0;
        border-bottom: 1px solid #ebebeb;

        &.is-on {
          .label:first-child {
            color: #3eaa08;
          }
        }
        &:not(.is-on) {
          .label:last-child {
            color: #0e90a5;
          }
        }

        .label {
          font-size: 12px;
          font-weight: 500;
          color: #555;
          line-height: 18px;
          vertical-align: top;
          display: inline-block;
          cursor: pointer;
          transition: color 0.15s;
        }

        .ba-switch-wrap {
          vertical-align: top;
          padding: 0 5px;
          display: inline-block;
        }

        .ba-switch {
          margin: 0;
        }
      }

      .switch:not(.is-on) + .list {
        li svg path {
          stroke: #0E90A5;
        }

        li.refresh,
        li.duration,
        li.mptilabel {
          color: #c1c1c1;

          svg path {
            opacity: 0;
          }
        }
      }

      .list {
        padding-top: 5px;
        li {
          position: relative;
          font-size: 12px;
          font-weight: 500;
          color: #555;
          line-height: 1;
          padding: 7px 0 7px 23px;
          transition: color 0.15s;

          svg {
            position: absolute;
            left: 1px;
            top: 5px;

            path {
              fill: none;
              stroke: #3eaa08;
              stroke-width: 2;
              stroke-linecap: round;
              stroke-linejoin: round;
              transition: stroke 0.15s, opacity 0.15s;
            }
          }
        }
      }
    }

    &.group-options {
      .ui-input-item .ui_radio {
        max-width: 125px;
        white-space: nowrap;
        text-overflow: ellipsis;
        display: inline-block;
        overflow: hidden;
      }
    }

    &.show {
      &-enter-active,
      &-leave-active {
        transition: transform 0.2s ease,
                    opacity 0.2s ease;
      }

      &-enter,
      &-leave-to {
        opacity: 0;
        pointer-events: none;
        transform: translate3d(-40px, 0, 0);
      }
    }
  }
}
</style>
